import { BaseEntity, Required } from "../common/base-entity";
import { Telephone } from "../shared/telephone";
import { TelephoneTypes } from "../shared/telephone-types";
import { AddressTypes } from "../shared/address-types";
import { Address } from "../shared/address";
import { Identification } from "../shared/identification";
import {
  DpBooleanValueTypes,
  IdentificationOptions,
  identificationStatus,
} from "../shared/constants";
import { CirfUdf } from "./cirf-udf";
import moment from "moment";

export class CirfClientInfo extends BaseEntity {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;

  birthDate: string;

  isCanadianResident: string;
  email: string;
  firstTimeHomeBuyer: string;

  residentStatus: string;
  spousalStatus: string;
  isFamilyResidence: string;
  isSpouseOnTitle: string;
  spouseName: string;
  occupation: string;
  telephones: Telephone[] = [];
  addresses: Address[] = [];
  identifications: Identification[] = [];
  // This flag indicates if the user checked "I do not have all details for this client" checkbox. It's used to update the required attributes of all fields
  allowMissingFields: boolean;
  agreedToUploadDL: string; //YES or NO

  // clientInfoFromDL indicates that we collected the information from a driver license. And it's used to mark some fields as read only
  isClientInfoFromDL: boolean;
  showAllFields: boolean;

  spouseId: string;
  cirfClientInfoUdfs: CirfUdf[] = [];

  hasErrors: boolean;
  hasPersonalDetailsSectionErrors: boolean;
  hasContactDetailsSectionErrors: boolean;
  hasIdVerification1SectionErrors: boolean;
  hasIdVerification2SectionErrors: boolean;

  constructor(cirfClientInfo?: CirfClientInfo) {
    super(cirfClientInfo);
    if (cirfClientInfo) {
      this.telephones = [];
      if (cirfClientInfo.telephones) {
        this.populatePhones(cirfClientInfo, [
          TelephoneTypes.home,
          TelephoneTypes.cell,
          TelephoneTypes.work,
        ]);
      }
      this.addresses = [];
      if (cirfClientInfo.addresses) {
        cirfClientInfo.addresses.forEach((address) => {
          this.addresses.push(new Address(address));
        });
      }
      this.identifications = [];
      if (
        cirfClientInfo.identifications &&
        cirfClientInfo.identifications.length == 2
      ) {
        this.addIdentificaion(
          identificationStatus.primary,
          cirfClientInfo.identifications,
          0
        );
        this.addIdentificaion(
          identificationStatus.secondary,
          cirfClientInfo.identifications,
          1
        );
      }
      if (cirfClientInfo.birthDate) {
        // Format date coming from Unity (if provided). Replaces all '/' with '-' for datepicker display
        this.birthDate = String(cirfClientInfo.birthDate).replace(/\//g, "-");
        // DPPMP-55105: Format date when entered via datepicker
        if (moment.isMoment(cirfClientInfo.birthDate)) {
          // UTC makes it so users timezone does not affect Date object
          this.birthDate = moment
            .utc(cirfClientInfo.birthDate)
            .format("YYYY/MM/DD");
        }
      }
      this.cirfClientInfoUdfs = [];
      if (cirfClientInfo.cirfClientInfoUdfs) {
        cirfClientInfo.cirfClientInfoUdfs.forEach((udf) => {
          this.cirfClientInfoUdfs.push(new CirfUdf(udf));
        });
      }
    } else {
      this.hasErrors = false;
    }
    if (!this.firstName) {
      this.firstName = "";
    }
    if (!this.middleName) {
      this.middleName = "";
    }
    if (!this.lastName) {
      this.lastName = "";
    }
    if (!this.gender) {
      this.gender = "";
    }
    if (!this.isCanadianResident) {
      this.isCanadianResident = "";
    }
    if (!this.email) {
      this.email = "";
    }
    if (!this.firstTimeHomeBuyer) {
      this.firstTimeHomeBuyer = "";
    }
    if (!this.birthDate) {
      this.birthDate = null;
    }
    if (!this.occupation) {
      this.occupation = "";
    }
    if (!this.residentStatus) {
      this.residentStatus = "";
    }
    if (!this.spousalStatus) {
      this.spousalStatus = "";
    }
    if (!this.isFamilyResidence) {
      this.isFamilyResidence = "";
    }
    if (!this.isSpouseOnTitle) {
      this.isSpouseOnTitle = "";
    }
    if (!this.spouseName) {
      this.spouseName = "";
    }
    if (!this.spouseId) {
      this.spouseId = "";
    }
    if (!this.telephones || this.telephones.length == 0) {
      this.telephones = [];
      this.initializePhones([
        TelephoneTypes.home,
        TelephoneTypes.cell,
        TelephoneTypes.work,
      ]);
    }
    if (!this.addresses || this.addresses.length == 0) {
      this.addresses = [];
      this.initializeAddresses([AddressTypes.clientIdBusiness]);
    }
    if (!this.identifications || this.identifications.length == 0) {
      this.identifications = [];
      let primaryIdentification = new Identification();
      primaryIdentification.identificationStatus = identificationStatus.primary;
      this.identifications.push(primaryIdentification);
      let secondaryIdentification = new Identification();
      secondaryIdentification.identificationStatus =
        identificationStatus.secondary;
      this.identifications.push(secondaryIdentification);
    }
  }

  addIdentificaion(
    status: string,
    identifications: Identification[],
    index: number
  ): void {
    let identification = identifications.find(
      (item) => item.identificationStatus == status
    );
    if (identification) {
      this.identifications.push(new Identification(identification));
    } else {
      identification = new Identification(identifications[index]);
      identification.identificationStatus = status;
      this.identifications.push(identification);
    }
  }

  // need to populate phone array in specific order which is needed by the form
  // TODO: when 'mappers' implemented between form and model then this can be removed
  populatePhones(cirfClientInfo: CirfClientInfo, phoneTypes: string[]): void {
    for (const phoneType of phoneTypes) {
      let phone: Telephone = cirfClientInfo.telephones.find(
        (t: Telephone) => t.phoneTypeCode === phoneType
      );
      if (phone) {
        this.telephones.push(new Telephone(phone));
      } else {
        phone = new Telephone();
        phone.phoneTypeCode = phoneType;
        this.telephones.push(phone);
      }
    }
  }

  get fullName(): string {
    return (
      (this.firstName ? this.firstName + " " : "") +
      (this.middleName ? this.middleName + " " : "") +
      (this.lastName ? this.lastName : "")
    );
  }

  initializePhones(phoneTypes: string[]): void {
    for (let phoneType of phoneTypes) {
      let phone: Telephone = this.telephones.find(
        (t: Telephone) => t.phoneTypeCode === phoneType
      );

      if (!phone) {
        phone = new Telephone();

        phone.phoneTypeCode = phoneType;

        this.telephones.push(phone);
      }
    }
  }

  get homePhone(): string {
    return this.getPhone(TelephoneTypes.home).telephoneNumber;
  }

  set homePhone(phone: string) {
    this.getPhone(TelephoneTypes.home).telephoneNumber = phone;
  }

  get cellPhone(): string {
    return this.getPhone(TelephoneTypes.cell).telephoneNumber;
  }

  set cellPhone(phone: string) {
    this.getPhone(TelephoneTypes.cell).telephoneNumber = phone;
  }

  get workPhone(): string {
    return this.getPhone(TelephoneTypes.work).telephoneNumber;
  }

  set workPhone(phone: string) {
    this.getPhone(TelephoneTypes.work).telephoneNumber = phone;
  }

  getPhone(type: string): Telephone {
    if (!this.telephones) {
      this.telephones = [];
    }

    let phone: Telephone = this.telephones.find(
      (t: Telephone) => t.phoneTypeCode === type
    );
    if (!phone) {
      phone = new Telephone();
      phone.phoneTypeCode = type;
      this.telephones.push(phone);
    }
    return phone;
  }

  initializeAddresses(addressTypes: string[]): void {
    for (let addressType of addressTypes) {
      let address: Address = this.addresses.find((address: Address) => {
        return address.addressTypeCode === addressType;
      });

      if (!address) {
        address = new Address();
        address.addressTypeCode = addressType;
        this.addresses.push(address);
      }
    }
  }

  getAddress(type: string): Address {
    if (!this.addresses) {
      this.addresses = [];
    }
    let address: Address = this.addresses.find((address: Address) => {
      return address.addressTypeCode === type;
    });
    if (!address) {
      address = new Address();
      address.addressTypeCode = type;
      this.addresses.push(address);
    }
    return address;
  }

  get businessAddress(): Address {
    return this.getAddress(AddressTypes.clientIdBusiness);
  }

  isPermanentResident(): boolean {
    return this.residentStatus == "PERMANENT_RESIDENT";
  }

  isCanadianResidentYes(): boolean {
    return this.isCanadianResident == DpBooleanValueTypes.YES;
  }

  isSpousalStatusMarried(): boolean {
    return this.spousalStatus == "MARRIED";
  }

  isSpouseOnTitleYesOrNo(): boolean {
    return (
      this.isSpouseOnTitle == DpBooleanValueTypes.YES ||
      this.isSpouseOnTitle == DpBooleanValueTypes.NO
    );
  }

  isSpouseNameVisible(): boolean {
    return this.isSpouseOnTitleYesOrNo() && this.isSpousalStatusMarried();
  }

  isDriverLicenceSelected(index: number): boolean {
    return (
      this.identifications?.length > 0 &&
      this.identifications[index]?.identificationTypeCode.startsWith("Driver's")
    );
  }

  get entityName(): string {
    return "cirfClientInfo";
  }

  isAgreedToUploadDLNo(): boolean {
    return this.agreedToUploadDL == DpBooleanValueTypes.NO;
  }
}
